import {Platform} from 'react-native';
import {
  differenceInCalendarDays,
  format,
  getMonth,
  isValid,
  parseISO,
  setMonth,
  subMonths,
} from 'date-fns';

import VisitWebsitePopover from '/component/partial/VisitWebsitePopover';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {openURL, openURLInNewTabOnWeb} from '/util/linking.util';

export const CAB_MAX_CLIENTS = 15;
export const CAB_TIER_SIZE = 5;

/**
 * Returns the bonus period for CAB.
 * Bonus period should end on the previous month before the eligibilityEndDate month
 * and start 2 months prior to that. ie, if eligibilityEndDate = february, bonus period = November - January.
 * @returns {string} returns the bonus period for CAB
 * @param eligibilityEndDate
 */
export function calculateBonusPeriod(eligibilityEndDate: string): string {
  const dateUtil = getDateUtilDefaultInstance();
  const endDate = toSafeDate(eligibilityEndDate);
  if (!endDate) {
    return '--';
  }
  const endDateMonth = getMonth(endDate);
  let setBonusEndMonthIndex;
  let setBonusStartMonthIndex;
  if (endDateMonth === 0) {
    setBonusEndMonthIndex = 11;
  } else {
    setBonusEndMonthIndex = endDateMonth - 1;
  }
  setBonusStartMonthIndex = setBonusEndMonthIndex - 2;
  const setBonusEndMonth = setMonth(endDate, setBonusEndMonthIndex);
  const setBonusStartMonth = setMonth(endDate, setBonusStartMonthIndex);
  return `${dateUtil.formatDate(
    setBonusStartMonth,
    'MMMM',
  )} - ${dateUtil.formatDate(setBonusEndMonth, 'MMMM')}`;
}

const toSafeDate = (value?: string): Date | null => {
  if (!value) {
    return null;
  }

  const parsedDate = parseISO(value);
  if (isValid(parsedDate)) {
    return parsedDate;
  }

  const fallbackDate = new Date(value);
  return isValid(fallbackDate) ? fallbackDate : null;
};

export const getCappedClientCount = (clientCount = 0): number => {
  return Math.max(0, Math.min(clientCount, CAB_MAX_CLIENTS));
};

export const getCompletedTierCount = (clientCount = 0): number => {
  return Math.floor(getCappedClientCount(clientCount) / CAB_TIER_SIZE);
};

export const getTierProgress = (clientCount = 0, tierIndex = 0): number => {
  const tierStart = tierIndex * CAB_TIER_SIZE;
  return Math.max(
    0,
    Math.min(getCappedClientCount(clientCount) - tierStart, CAB_TIER_SIZE),
  );
};

export const getTierSlots = (tierIndex = 0): number[] => {
  const start = tierIndex * CAB_TIER_SIZE + 1;
  return [0, 1, 2, 3, 4].map(offset => start + offset);
};

export const calculateCABPeriod = (
  eligibilityStartDate?: string,
  eligibilityEndDate?: string,
): string => {
  const startDate = toSafeDate(eligibilityStartDate);
  const endDate = toSafeDate(eligibilityEndDate);

  if (startDate && endDate) {
    return `${format(startDate, 'MMMM')} - ${format(endDate, 'MMMM')}`;
  }

  if (endDate) {
    const bonusStart = subMonths(endDate, 2);
    return `${format(bonusStart, 'MMMM')} - ${format(endDate, 'MMMM')}`;
  }

  return '--';
};

export const calculateDaysLeft = (
  currentDate?: string,
  eligibilityEndDate?: string,
): number => {
  const currentDateObj = toSafeDate(currentDate);
  const eligibilityEndDateObj = toSafeDate(eligibilityEndDate);

  if (!currentDateObj || !eligibilityEndDateObj) {
    return 0;
  }

  return Math.max(
    differenceInCalendarDays(eligibilityEndDateObj, currentDateObj),
    0,
  );
};

export const shouldShowCABBanner = (
  eligibilityEndDate?: string,
  currentDate?: string,
): boolean => {
  const dateUtil = getDateUtilDefaultInstance();
  if (!eligibilityEndDate) {
    return false;
  }

  if (currentDate) {
    const currentDateObj = toSafeDate(currentDate);
    const eligibilityEndDateObj = toSafeDate(eligibilityEndDate);

    return !!(
      currentDateObj &&
      eligibilityEndDateObj &&
      currentDateObj <= eligibilityEndDateObj
    );
  }

  return dateUtil.isDateSameOrBefore(eligibilityEndDate);
};

export const motivationalText = {
  [0]: 'Get Started by signing up your first Client this month.',
  [1]: 'Way to get your first Client this month, now keep going.',
  [2]: 'You are just 1 Client away from earning your first CAB Bonus this month.',
  [3]: 'You earned the first tier. Now see if you can get 2 more Clients and earn the second tier.',
  [4]: 'You are just 1 Client away from earning your second CAB Bonus this month.',
  [5]: 'Congratulations on earning the full CAB amount this month. Only 2 more Clients to max out your Client Support Bonus+ as well.',
};

export const generateOnLearnMore = (
  {popoverShow}: any,
  currentScreen: any,
  URL: string,
) => {
  return () => {
    if (Platform.OS === 'web') {
      openURLInNewTabOnWeb(URL);
      return;
    }

    popoverShow(VisitWebsitePopover, {
      currentScreen: currentScreen,
      destinationURL: URL,
      onSubmit: () => openURL('url', URL),
    });
  };
};
